/**
 * Registers the two Home contributors (`plannerSession` and `mapCamera`) to
 * the session restore service.
 */

import {
    type MapCameraSnapshot,
    type PlannerSessionSnapshot,
} from "../services/sessionRestore/sessionRestore.types";
import { useSessionRestoreContributor } from "./useSessionRestoreContributor";

/**
 * Options for {@link useHomeSessionRestore}.
 */
export interface UseHomeSessionRestoreOptions {

    /** Returns the current planner session snapshot, or `null` when nothing to save. */
    "getPlannerSession": () => PlannerSessionSnapshot | null;

    /** Applies a planner session snapshot back to Home state. */
    "restorePlannerSession": ( snapshot: PlannerSessionSnapshot ) => void;

    /** Returns the current map camera, or `null` when not ready / not needed. */
    "getMapCamera": () => MapCameraSnapshot | null;

    /** Moves the map camera back to the saved position. */
    "restoreMapCamera": ( camera: MapCameraSnapshot ) => void;
}

/**
 * Wires Home's planner and camera state into the session restore mechanism.
 * Should be called once at the top of `Home.tsx`.
 *
 * @param options - Capture and restore callbacks for the planner and the camera.
 */
export function useHomeSessionRestore( options: UseHomeSessionRestoreOptions ): void {
    const { getPlannerSession, restorePlannerSession, getMapCamera, restoreMapCamera } = options;

    useSessionRestoreContributor<PlannerSessionSnapshot>(
        "plannerSession",
        getPlannerSession,
        restorePlannerSession,
        { "priority": 10 },
    );

    useSessionRestoreContributor<MapCameraSnapshot>(
        "mapCamera",
        getMapCamera,
        restoreMapCamera,
        { "priority": 20 },
    );
}
